import { ShieldCheck, Users } from "lucide-react";
import { type FormEvent, useState } from "react";

import { api, ApiError } from "../api/client";
import type { AuthSession } from "../api/types";
import { attentionSelector, designate } from "../components/attention";
import { Checkbox } from "../components/Checkbox";
import { Button, Field, inputClass } from "../components/ui";
import { t } from "../i18n/i18n";
import { LicenceFrame } from "../licence/ActivationScreen";
import { AuthTitle, codeComplete, CodeInput, FormError, NewPasswordFields } from "./AuthForm";
import { authErrorText, LOGIN_PATTERN, passwordProblem } from "./authText";
import { useSignIn } from "./LoginScreen";
import { Waiting } from "../components/Waiting";

/** Proposition de bascule (docs/17 §8.3) : licence en mode hérité qui a reçu un code d'amorçage. « Plus tard » est
 *  absent en mode obligatoire ; ailleurs, la proposition revient à la session suivante du navigateur. */
export function BootstrapOffer({ required, onCreate, onLater }: { required: boolean; onCreate: () => void; onLater: () => void }) {
  return (
    <LicenceFrame>
      <AuthTitle icon={<Users size={16} />}>{t("Comptes nominatifs")}</AuthTitle>
      <p className="mt-1.5 text-ui leading-relaxed text-ink-soft">
        {t("Generex passe aux comptes nominatifs : chacun se connecte avec son identifiant et son mot de passe, retrouve ses dossiers et choisit avec qui il les partage. La personne désignée par l'unité crée d'abord le compte de l'administrateur avec le code d'amorçage reçu de BELLICOM ; l'administrateur crée ensuite les comptes des autres utilisateurs. Les dossiers, ordres de bataille et terrains déjà enregistrés restent accessibles à tous les comptes de l'unité.")}
      </p>
      <div className="mt-4 space-y-2">
        <Button className="w-full" onClick={onCreate} autoFocus>
          {t("Créer le compte administrateur")}
        </Button>
        {!required && (
          <Button variant="secondary" className="w-full" onClick={onLater}>
            {t("Plus tard")}
          </Button>
        )}
      </div>
    </LicenceFrame>
  );
}

/** Compte administrateur de l'unité (amorçage, §5.2) : code d'amorçage remis hors de Generex, identité, mot de passe et
 *  notice. Réussite : la session est ouverte et le code de secours de l'unité est rendu (étape suivante). */
export function BootstrapScreen({
  onDone,
  onBack,
  onTaken,
  showNotice,
}: {
  onDone: (result: AuthSession) => void;
  onBack?: () => void;
  /** Compte administrateur créé entre-temps sur un autre poste : retour à la connexion. */
  onTaken: () => void;
  /** Ouvre la notice de protection des données. */
  showNotice: () => void;
}) {
  const [code, setCode] = useState("");
  const [unitName, setUnitName] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [appointment, setAppointment] = useState("");
  const [login, setLogin] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [notice, setNotice] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  // Comme la connexion : un refus « cookie du poste pas encore migré » (§4.4) fait relire la licence, puis rejouer.
  const create = useSignIn(() =>
    api.auth.bootstrap({
      bootstrap_code: code,
      unit_name: unitName.trim() || null,
      display_name: displayName.trim(),
      appointment: appointment.trim() || null,
      login: login.trim(),
      password,
    }),
  );

  // Premier préalable manquant (R12) : le bouton reste cliquable et désigne le champ attendu.
  const missing = !codeComplete(code)
    ? "auth-bootstrap-code"
    : displayName.trim().length < 2
      ? "auth-display-name"
      : !LOGIN_PATTERN.test(login.trim())
        ? "auth-login"
        : passwordProblem(password, confirm)
          ? password.length < 12
            ? "auth-new-password"
            : "auth-confirm-password"
          : !notice
            ? "auth-notice"
            : null;
  const blocked = () => {
    if (!missing) return;
    if (missing === "auth-new-password" || missing === "auth-confirm-password") setProblem(passwordProblem(password, confirm));
    else if (missing === "auth-login" && login.trim()) setProblem(t("Identifiant : lettres sans accent, chiffres, point, tiret ; 3 à 40 caractères."));
    designate(attentionSelector(missing));
  };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (missing) {
      blocked();
      return;
    }
    setProblem(null);
    if (create.isPending) return;
    create.mutate(undefined, {
      onSuccess: (result) => {
        setPassword("");
        setConfirm("");
        onDone(result);
      },
      onError: (error) => {
        setPassword("");
        setConfirm("");
        setProblem(authErrorText(error, "bootstrap"));
      },
    });
  };
  const done = create.error instanceof ApiError && create.error.code === "BOOTSTRAP_DONE";

  return (
    <LicenceFrame wide>
      <AuthTitle icon={<ShieldCheck size={16} />}>{t("Compte administrateur de l'unité")}</AuthTitle>
      <form onSubmit={submit} className="mt-4 space-y-3">
        <Field label={t("Code d'amorçage")} hint={t("Remis par BELLICOM, ou par l'exploitant de votre réseau, à la personne désignée par l'unité. Il ne sert qu'une fois.")}>
          <CodeInput value={code} onChange={setCode} attention="auth-bootstrap-code" autoFocus />
        </Field>
        {/* Ordre du §8.3 : nom de l'unité, grade et nom, fonction, identifiant. */}
        <Field label={t("Nom de l'unité (facultatif)")} hint={t("Ex. : 2e RIMa · PC GTIA")}>
          <input className={inputClass} value={unitName} maxLength={80} onChange={(e) => setUnitName(e.target.value)} />
        </Field>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label={t("Grade et nom")} hint={t("Ex. : CNE MARTIN. C'est le nom qui signe vos actions (journal DIREX, ordres reçus, transmissions).")}>
            <input data-attention="auth-display-name" className={inputClass} value={displayName} maxLength={80} onChange={(e) => setDisplayName(e.target.value)} />
          </Field>
          <Field label={t("Fonction (facultatif)")} hint={t("Ex. : chef BOI")}>
            <input className={inputClass} value={appointment} maxLength={80} onChange={(e) => setAppointment(e.target.value)} />
          </Field>
        </div>
        <Field label={t("Identifiant")} hint={t("Ex. : cne.martin. Lettres sans accent, chiffres, point, tiret ; 3 à 40 caractères.")}>
          <input
            data-attention="auth-login"
            className={inputClass}
            value={login}
            maxLength={40}
            onChange={(e) => setLogin(e.target.value.toLowerCase())}
            autoComplete="username"
            autoCapitalize="none"
            spellCheck={false}
          />
        </Field>
        <NewPasswordFields password={password} confirm={confirm} onPassword={setPassword} onConfirm={setConfirm} label={t("Mot de passe")} />
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <Checkbox data-attention="auth-notice" checked={notice} onChange={setNotice} label={t("J'ai lu la notice de protection des données")} />
          <Button variant="link" onClick={showNotice}>
            {t("Lire la notice")}
          </Button>
        </div>
        <FormError message={problem} />
        <div className="space-y-2 pt-1">
          {done ? (
            <Button className="w-full" onClick={onTaken}>
              {t("Se connecter")}
            </Button>
          ) : (
            <Button type="submit" className="w-full" disabled={create.isPending} blocked={Boolean(missing)} onBlocked={blocked}>
              {create.isPending ? <Waiting>{t("Création du compte…")}</Waiting> : t("Créer le compte")}
            </Button>
          )}
          {onBack && (
            <Button variant="secondary" className="w-full" onClick={onBack}>
              {t("Retour")}
            </Button>
          )}
        </div>
      </form>
    </LicenceFrame>
  );
}
